import { EnvironmentRegistry, EnvironmentSpec } from './types.js';
import { buildNeonHaven } from './neonhaven.js';
import { buildHelionCore } from './reactor.js';
import { buildDuneworks } from './duneworks.js';
import { buildStormline } from './stormline.js';
import { buildAnchorage9 } from './shipyard.js';
import { buildAuroraReach } from './aurora.js';
import { buildTheAscent } from './ascent.js';
import { buildGenericEnv } from './generic.js';
import { EnvironmentId } from '@voidline/shared';

/**
 * Environment registry. Every EnvironmentId must resolve — bespoke builders where
 * they exist, tuned generic variants otherwise, so procedural tracks can always render.
 */

export const ENVIRONMENTS: EnvironmentRegistry = {
  neonhaven: {
    label: 'Neon Haven',
    blurb: 'Rain-soaked megacity. Holo-arches, glass towers, a million lit windows.',
    edgeColor: 0x31c8ff,
    edgeColorAlt: 0xff2d6f,
    wallColor: 0x31c8ff,
    fogColor: 0x0a0c18,
    fogDensity: 0.0018,
    build: buildNeonHaven,
  },
  reactor: {
    label: 'Helion Core',
    blurb: 'Inside a continental fusion reactor. Heat, gantries, containment rings.',
    edgeColor: 0xffa028,
    edgeColorAlt: 0xff5a3c,
    wallColor: 0xffa028,
    fogColor: 0x1c0a04,
    fogDensity: 0.0025,
    build: buildHelionCore,
  },
  duneworks: {
    label: 'The Duneworks',
    blurb: 'A desert world littered with alien machines a million years dead.',
    edgeColor: 0xffd25e,
    edgeColorAlt: 0x52e89a,
    wallColor: 0xffd25e,
    fogColor: 0x261808,
    fogDensity: 0.0016,
    build: buildDuneworks,
  },
  stormline: {
    label: 'Stormline',
    blurb: 'A circuit strung through the eternal storm of a gas giant.',
    edgeColor: 0x9a5cff,
    edgeColorAlt: 0x31c8ff,
    wallColor: 0x9a5cff,
    fogColor: 0x181030,
    fogDensity: 0.0027,
    build: buildStormline,
  },
  shipyard: {
    label: 'Anchorage-9',
    blurb: 'An orbital shipyard. Capital hulls slide past on invisible rails.',
    edgeColor: 0x52e89a,
    edgeColorAlt: 0x31c8ff,
    wallColor: 0x52e89a,
    fogColor: 0x04070d,
    fogDensity: 0.0007,
    build: buildAnchorage9,
  },
  aurora: {
    label: 'Aurora Reach',
    blurb: 'Frozen night. The sky burns green and violet over black ice.',
    edgeColor: 0x5effe0,
    edgeColorAlt: 0x9a5cff,
    wallColor: 0x5effe0,
    fogColor: 0x070d16,
    fogDensity: 0.0012,
    build: buildAuroraReach,
  },
  ascent: {
    label: 'The Ascent',
    blurb: 'From launchpad haze to hard vacuum in one climb.',
    edgeColor: 0x31c8ff,
    edgeColorAlt: 0xffd25e,
    wallColor: 0x31c8ff,
    fogColor: 0x27384e,
    fogDensity: 0.0016,
    build: buildTheAscent,
  },
  voidgate: {
    label: 'Voidgate',
    blurb: 'Beyond the last beacon. Geometry stops asking permission.',
    edgeColor: 0xc8f34c,
    edgeColorAlt: 0xb366ff,
    wallColor: 0xb366ff,
    fogColor: 0x08040e,
    fogDensity: 0.0015,
    build: (scene, track, q) =>
      buildGenericEnv(scene, track, q, {
        skyTop: 0x05020a,
        skyBottom: 0x140a24,
        starColor: 0xd0ff88,
        nebula: 0x6a1a8a,
        structureColor: 0x140f1e,
        structureEmissive: 0xb366ff,
        hemiSky: 0x3a2a5a,
        hemiGround: 0x08040e,
        sunColor: 0xcc88ff,
        sunDir: [0, 1, 0],
        structureCount: 34,
        structureStyle: 'rings',
        floorY: null,
      }, 88),
  },
};

export function getEnvironment(id: EnvironmentId): EnvironmentSpec {
  return ENVIRONMENTS[id] ?? ENVIRONMENTS.voidgate;
}
